import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Check, X, AlertTriangle, Clock, Settings2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { useVeRuta } from '@/hooks/useMenu';
import { cn } from '@/lib/cn';
import * as inspectorApi from '@/api/inspector.api';
import type { Hallazgo, Prioridad } from '@/api/inspector.api';

/**
 * LA BANDEJA DEL INSPECTOR — lo que encontró leyendo, ya depurado.
 *
 * Esteban la definió así: *"él es mis ojos, ante el volumen de información de
 * saturación, y me la tiene que depurar para entregarme la información que
 * necesito... porque me representa dinero."*
 *
 * ═══ POR QUÉ CADA FILA MUESTRA EL «POR QUÉ» ═══
 *
 * Una prioridad sin razón es una afirmación que no se puede discutir. Si el
 * Inspector dice ALTA y el dueño no entiende por qué, sólo le quedan dos
 * opciones: obedecer a ciegas o dejar de leerlo. Las dos son malas. Con la
 * razón al lado, puede corregirlo — y corregirlo es lo que lo hace confiable.
 *
 * ═══ EL RUIDO NO SE ESCONDE, SE PLIEGA ═══
 *
 * Lo que la depuración bajó a RUIDO sale de la bandeja, pero hay un botón para
 * verlo. Esconderlo del todo sería pedirle al dueño que confíe en un filtro que
 * no puede auditar, cuando lo que se le está filtrando es plata.
 */

const ORDEN_PRIORIDAD: Prioridad[] = ['ALTA', 'MEDIA', 'BAJA', 'SIN_REVISAR', 'RUIDO'];

const ETIQUETA: Record<Prioridad, string> = {
  ALTA: 'Alta',
  MEDIA: 'Media',
  BAJA: 'Baja',
  RUIDO: 'Ruido',
  SIN_REVISAR: 'Sin revisar',
};

/** El color dice el tamaño de un vistazo. Rojo sólo para lo que cuesta hoy. */
const TONO: Record<Prioridad, string> = {
  ALTA: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
  MEDIA: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
  BAJA: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  RUIDO: 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500',
  SIN_REVISAR: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200',
};

function cuando(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('es-DO', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Numerito({
  etiqueta,
  valor,
  icono,
  alerta,
}: {
  etiqueta: string;
  valor: number;
  icono: React.ReactNode;
  alerta?: boolean;
}) {
  return (
    <Card>
      <CardBody className="flex items-center gap-3">
        <span
          className={cn(
            'grid h-10 w-10 place-items-center rounded-lg',
            alerta && valor > 0
              ? 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300'
              : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
          )}
        >
          {icono}
        </span>
        <span>
          <span className="block text-2xl font-semibold leading-none">{valor}</span>
          <span className="text-xs text-slate-500 dark:text-slate-400">{etiqueta}</span>
        </span>
      </CardBody>
    </Card>
  );
}

function FilaHallazgo({
  h,
  onEstado,
  ocupado,
}: {
  h: Hallazgo;
  onEstado: (id: string, estado: 'CUMPLIDO' | 'DESCARTADO') => void;
  ocupado: boolean;
}) {
  return (
    <div className="flex flex-col gap-2 border-b border-slate-100 py-3 last:border-0 dark:border-slate-800 sm:flex-row sm:items-start sm:gap-4">
      <div className="flex shrink-0 items-center gap-2">
        <Badge className={TONO[h.prioridad]}>{ETIQUETA[h.prioridad]}</Badge>
        {h.vencido && (
          <Badge className="bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200">
            Vencido
          </Badge>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{h.descripcion}</p>
        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
          <span className="font-medium">{h.conQuien}</span>
          {h.tema ? <> · {h.tema}</> : null} · {cuando(h.fechaCompromiso)} ·{' '}
          <span className="font-mono">{h.codigo}</span>
        </p>
        {/* La razón de la prioridad. Sin esto el dueño no puede discutirla. */}
        {h.porQue && (
          <p className="mt-1 text-xs italic text-slate-500 dark:text-slate-400">“{h.porQue}”</p>
        )}
        {!h.depuradoAt && (
          <p className="mt-1 text-xs text-sky-600 dark:text-sky-300">
            Todavía no pasó por la depuración.
          </p>
        )}
      </div>

      <div className="flex shrink-0 gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={ocupado}
          onClick={() => onEstado(h.id, 'CUMPLIDO')}
        >
          <Check className="h-4 w-4" /> Hecho
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={ocupado}
          onClick={() => onEstado(h.id, 'DESCARTADO')}
        >
          <X className="h-4 w-4" /> No va
        </Button>
      </div>
    </div>
  );
}

export default function Inspector() {
  const [verRuido, setVerRuido] = useState(false);
  const toast = useToast();
  // Los ajustes de Athena son de quien administra el sistema.
  const veAjustes = useVeRuta('/inspector/ajustes');
  const qc = useQueryClient();

  const resumen = useQuery({
    queryKey: ['inspector', 'resumen'],
    queryFn: inspectorApi.getResumenInspector,
  });

  const hallazgos = useQuery({
    queryKey: ['inspector', 'hallazgos', verRuido],
    queryFn: () => inspectorApi.getHallazgos({ incluirRuido: verRuido, limite: 200 }),
  });

  const cambiar = useMutation({
    mutationFn: ({ id, estado }: { id: string; estado: 'CUMPLIDO' | 'DESCARTADO' }) =>
      inspectorApi.cambiarEstadoHallazgo(id, estado),
    onSuccess: (_d, v) => {
      toast.success(v.estado === 'CUMPLIDO' ? 'Marcado como hecho.' : 'Descartado.');
      void qc.invalidateQueries({ queryKey: ['inspector'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const lista = hallazgos.data?.hallazgos ?? [];
  const porPrioridad = ORDEN_PRIORIDAD.map((p) => ({
    prioridad: p,
    items: lista.filter((h) => h.prioridad === p),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Inspector"
        description="Lee tus conversaciones sin marcarlas como vistas y te deja acá lo que no puede esperar."
        action={
          veAjustes ? (
            <Link to="/inspector/ajustes">
              <Button variant="secondary" size="sm">
                <Settings2 className="h-4 w-4" /> Ajustes
              </Button>
            </Link>
          ) : undefined
        }
      />

      {resumen.isPending ? (
        <Spinner />
      ) : resumen.isError ? (
        <Card>
          <CardBody className="text-sm text-red-600 dark:text-red-300">
            No se pudo leer el resumen del Inspector.
          </CardBody>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Numerito
              etiqueta="Urgente hoy"
              valor={resumen.data.alta}
              icono={<AlertTriangle className="h-5 w-5" />}
              alerta
            />
            <Numerito
              etiqueta="Vencidos"
              valor={resumen.data.vencidos}
              icono={<Clock className="h-5 w-5" />}
              alerta
            />
            <Numerito
              etiqueta="Para después"
              valor={resumen.data.media + resumen.data.baja}
              icono={<Eye className="h-5 w-5" />}
            />
            <Numerito
              etiqueta="Sin revisar"
              valor={resumen.data.sinRevisar}
              icono={<Eye className="h-5 w-5" />}
            />
          </div>

          {/* Si nunca depuró, se dice. Un reporte sin depurar parece depurado. */}
          {!resumen.data.ultimaDepuracion && (
            <Card>
              <CardBody className="text-sm text-amber-700 dark:text-amber-300">
                Todavía no corrió la depuración. Lo que ves está sin priorizar: puede tener
                ruido. Falta cargar la clave de IA en Ajustes para que el Inspector pueda
                analizar.
              </CardBody>
            </Card>
          )}
        </>
      )}

      <Card>
        <CardBody className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              Lo que encontró
            </p>
            <Button size="sm" variant="ghost" onClick={() => setVerRuido((v) => !v)}>
              {verRuido ? 'Ocultar ruido' : 'Ver también el ruido'}
            </Button>
          </div>

          {hallazgos.isPending ? (
            <Spinner />
          ) : hallazgos.isError ? (
            <p className="py-6 text-center text-sm text-red-600 dark:text-red-300">
              No se pudieron leer los hallazgos.
            </p>
          ) : lista.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">
              No hay nada abierto. Si el Inspector todavía no leyó tus conversaciones, revisa
              en Ajustes que esté encendido y que tenga canales asignados.
            </p>
          ) : (
            porPrioridad.map((g) => (
              <div key={g.prioridad} className="pt-2">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  {ETIQUETA[g.prioridad]} · {g.items.length}
                </p>
                {g.items.map((h) => (
                  <FilaHallazgo
                    key={h.id}
                    h={h}
                    ocupado={cambiar.isPending}
                    onEstado={(id, estado) => cambiar.mutate({ id, estado })}
                  />
                ))}
              </div>
            ))
          )}
        </CardBody>
      </Card>
    </div>
  );
}
